/**
 * AgentGuidedTour — in-app spotlight walkthrough for new agents.
 *
 * Powered by driver.js. Fires automatically on first login for role=agent,
 * and can be replayed manually from a "Tomar el tour" button.
 *
 * Each step-enter and step-complete is written to `/api/analytics/track` so
 * drop-off is visible in the admin analytics dashboard.
 *
 * Mirrors the 12-shot video tutorial script so agents get the same story
 * whether they watch the video or take the interactive tour.
 */
import { useCallback, useEffect, useRef } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import axios from 'axios';

import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = 'waka-agent-tour-seen';

// 12 tour steps mirroring the video tutorial shot list.
// Selectors are best-effort — steps skip themselves cleanly if the element
// isn't present (e.g. when the agent isn't on the exact page).
function buildSteps(language) {
  const es = language === 'es';
  const tt = (en, esT) => (es ? esT : en);
  return [
    {
      element: '[data-testid="agency-dashboard-page"], [data-testid="agent-dashboard"], body',
      popover: {
        title: tt('Welcome to WakaCasa', 'Bienvenido a WakaCasa'),
        description: tt(
          'This 90-second tour shows every feature you need as a verified agent. You can close it anytime and replay it from the "Take the tour" button.',
          'Este tour de 90 segundos te muestra cada función que necesitas como agente verificado. Puedes cerrarlo cuando quieras y volver a verlo desde el botón "Tomar el tour".'
        ),
        side: 'over',
        align: 'center',
      },
    },
    {
      element: '[data-testid="listing-usage-card"], [data-testid="listing-limit-card"]',
      popover: {
        title: tt('Listing usage', 'Uso de propiedades'),
        description: tt(
          'How many active listings you have vs your plan cap. Super Admin can grant you a custom limit for enterprise deals — you\'ll see it here.',
          'Cuántas propiedades activas llevas contra el límite de tu plan. El Super Admin puede darte un límite personalizado para deals empresariales — lo verás aquí.'
        ),
      },
    },
    {
      element: '[data-testid="profile-completion-card"], a[href="/profile"]',
      popover: {
        title: tt('Profile completion', 'Completitud del perfil'),
        description: tt(
          'A complete profile earns +10 Trust Score points and higher search ranking. Click here to add your photo, bio, phone, and languages.',
          'Un perfil completo suma +10 puntos al Trust Score y mejora tu posicionamiento en búsquedas. Haz clic aquí para agregar foto, bio, teléfono e idiomas.'
        ),
      },
    },
    {
      element: '[data-testid="verification-status-card"], a[href="/verification"]',
      popover: {
        title: tt('Get verified', 'Verifícate'),
        description: tt(
          'Upload 5 documents · GPT-4o AI + human review · valid 365 days. This is the +25 Trust Score jump that changes everything.',
          'Sube 5 documentos · IA GPT-4o + revisión humana · válido 365 días. Este es el salto de +25 puntos en Trust Score que cambia todo.'
        ),
      },
    },
    {
      element: '[data-testid="next-action-strip"]',
      popover: {
        title: tt("What to do now", '¿Qué hacer ahora?'),
        description: tt(
          "The platform picks your single most impactful next move — verify a document, respond to a stalled lead, boost a listing. No guesswork.",
          'La plataforma elige la acción más impactante para ti — verificar un documento, responder a un lead atascado, mejorar un anuncio. Sin adivinar.'
        ),
      },
    },
    {
      element: '[data-testid="submit-property-btn"], a[href="/submit"]',
      popover: {
        title: tt('Publish a property', 'Publica una propiedad'),
        description: tt(
          'Interactive map to drop the exact pin, auto-derived state, WakaCode auto-generated, and up to 3 Instagram post links per property.',
          'Mapa interactivo para poner el pin exacto, estado auto-derivado, WakaCode generado automáticamente, y hasta 3 links de Instagram por propiedad.'
        ),
      },
    },
    {
      element: '[data-testid="agency-bulk-upload-btn"], a[href="/agency/bulk-upload"]',
      popover: {
        title: tt('Bulk upload', 'Carga masiva'),
        description: tt(
          'Excel or Google Sheets → 50 properties in 5 minutes with live validation. Requires Independent or Pro tier.',
          'Excel o Google Sheets → 50 propiedades en 5 minutos con validación en vivo. Requiere Independent o Pro.'
        ),
      },
    },
    {
      element: '[data-testid="agency-tab-leads"], [data-testid="leads-tab"]',
      popover: {
        title: tt('Leads dashboard', 'Panel de Leads'),
        description: tt(
          'Reveals in amber, messages in green, Premium buyers with a gold crown. Stalled listings surface an actionable warning.',
          'Reveals en ámbar, mensajes en verde, compradores Premium con corona dorada. Los anuncios estancados muestran una alerta accionable.'
        ),
      },
    },
    {
      element: '[data-testid="transactions-menu-item"], a[href="/transactions"]',
      popover: {
        title: tt('Tracked Transactions', 'Transacciones Acompañadas'),
        description: tt(
          '11 stages from virtual tour to key delivery. You, the buyer (even from Miami), and a verified lawyer see the same timeline.',
          '11 etapas desde el tour virtual hasta la entrega de llaves. Tú, el comprador (incluso desde Miami) y un abogado verificado ven la misma línea de tiempo.'
        ),
      },
    },
    {
      element: '[data-testid="trust-score-pill"], [data-testid="trust-score-large"], body',
      popover: {
        title: tt('Your Trust Score', 'Tu Trust Score'),
        description: tt(
          'Public 0-100 score built from 7 signals: identity, listing approval, closed deals, Premium leads, profile, tenure, active listings.',
          'Puntaje público 0-100 con 7 señales: identidad, aprobación de anuncios, transacciones cerradas, leads Premium, perfil, antigüedad, anuncios activos.'
        ),
      },
    },
    {
      element: '[data-testid="feedback-widget-btn"]',
      popover: {
        title: tt('Talk to us', 'Escríbenos'),
        description: tt(
          'Bottom-left, always available. Rate, report a bug, tell us what to fix. During beta, every message is read and shipped against.',
          'Abajo a la izquierda, siempre disponible. Puntúa, reporta errores, dinos qué mejorar. En beta, cada mensaje se lee y se convierte en un cambio.'
        ),
        side: 'right',
        align: 'end',
      },
    },
    {
      element: 'body',
      popover: {
        title: tt("You're set", 'Listo'),
        description: tt(
          "That's the whole system. Buy with confidence. Sell with confidence. Welcome to WakaCasa.",
          'Ese es todo el sistema. Compra con confianza. Vende con confianza. Bienvenido a WakaCasa.'
        ),
        side: 'over',
        align: 'center',
      },
    },
  ];
}

/**
 * Custom hook: launches the tour and exposes a `start()` method.
 * The tour auto-starts once per agent on first login (localStorage-guarded).
 */
export function useAgentGuidedTour() {
  const { user, token } = useAuth();
  const { language } = useLanguage();
  const startedAtRef = useRef(0);

  const track = useCallback(
    async (event, payload = {}) => {
      if (!token) return;
      try {
        await axios.post(
          `${API}/analytics/track`,
          {
            event_type: event,
            metadata: { ...payload, tour: 'agent_v1' },
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch {
        /* silent — analytics best-effort */
      }
    },
    [token]
  );

  const start = useCallback(() => {
    if (typeof window === 'undefined') return;
    const steps = buildSteps(language);
    // Filter out steps whose selector doesn't match anything on the page
    const available = steps.filter((s) => {
      if (!s.element || s.element === 'body') return true;
      try {
        return !!document.querySelector(s.element);
      } catch {
        return false;
      }
    });
    if (available.length < 3) {
      // Not enough anchors on this page — fall back to the intro-only card
      available.unshift(steps[0]);
    }
    startedAtRef.current = Date.now();
    track('tour_started', { steps: available.length });
    const d = driver({
      showProgress: true,
      allowClose: true,
      overlayColor: '#0B192C',
      overlayOpacity: 0.68,
      stagePadding: 6,
      stageRadius: 12,
      popoverClass: 'waka-tour-popover',
      progressText: language === 'es' ? '{{current}} de {{total}}' : '{{current}} of {{total}}',
      nextBtnText: language === 'es' ? 'Siguiente →' : 'Next →',
      prevBtnText: language === 'es' ? '← Anterior' : '← Back',
      doneBtnText: language === 'es' ? 'Listo' : 'Done',
      steps: available,
      onHighlightStarted: (_el, step, opts) => {
        track('tour_step_entered', { index: opts.state.activeIndex, title: step.popover?.title });
      },
      onDeselected: (_el, step, opts) => {
        track('tour_step_completed', { index: opts.state.activeIndex, title: step.popover?.title });
      },
      onDestroyed: () => {
        const elapsed = Math.round((Date.now() - startedAtRef.current) / 1000);
        track('tour_ended', { elapsed_seconds: elapsed });
        localStorage.setItem(LS_KEY, '1');
      },
    });
    d.drive();
  }, [language, track]);

  // Auto-start on first login for agents (once per browser)
  useEffect(() => {
    if (!user || user.role !== 'agent') return;
    if (localStorage.getItem(LS_KEY)) return;
    const timer = setTimeout(() => {
      // Only auto-start if we're on a dashboard-ish page with anchors
      const hasAnchor =
        document.querySelector('[data-testid="agency-dashboard-page"]') ||
        document.querySelector('[data-testid="agent-dashboard"]') ||
        document.querySelector('[data-testid="listing-usage-card"]');
      if (hasAnchor) start();
    }, 1500);
    return () => clearTimeout(timer);
  }, [user, start]);

  return { start, hasSeen: !!localStorage.getItem(LS_KEY) };
}

/** Replayable "Take the tour" button — for the Agency Dashboard header. */
export default function TakeTourButton({ className = '' }) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { start } = useAgentGuidedTour();
  if (!user || user.role !== 'agent') return null;
  return (
    <button
      type="button"
      onClick={start}
      className={
        'inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white border border-[#D4AF37]/40 ' +
        'text-[#0B192C] text-sm font-medium hover:bg-[#D4AF37]/10 transition ' +
        className
      }
      data-testid="take-tour-btn"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5">
        <circle cx="12" cy="12" r="10" />
        <polygon points="10,8 16,12 10,16" fill="#D4AF37" />
      </svg>
      {language === 'es' ? 'Tomar el tour' : 'Take the tour'}
    </button>
  );
}
